import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { cn } from "@/lib/utils";
import { CTA_LINKS } from "@/data/content";

// Smooth scroll to an in-page anchor (works alongside Lenis)
export const goTo = (href) => {
  if (!href) return;
  if (href.startsWith("http")) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Fire a Meta Pixel event (safe if pixel not loaded)
const trackPixel = (event, params) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", event, params);
  }
};

export const handleCta = (target) => {
  // target can be a key of CTA_LINKS or a raw href
  const link = CTA_LINKS[target] || target;

  // Meta Pixel conversion events
  if (target === "checkout") {
    trackPixel("InitiateCheckout", { content_name: "Intensivo Transferência UNESP" });
  } else if (target === "whatsappSupport" || target === "whatsapp") {
    trackPixel("Lead", { content_name: "WhatsApp Suporte" });
  }

  goTo(link);
};

const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

// Signature masked line-by-line reveal for headings
// trigger="load" plays immediately (above-the-fold hero); "view" plays on scroll.
export const MaskReveal = ({ lines, className = "", lineClassName = "", delay = 0, trigger = "view" }) => {
  const anim = { y: "0%" };
  const viewProps =
    trigger === "load"
      ? { animate: anim }
      : { whileInView: anim, viewport: { once: true, margin: "-60px" } };
  return (
    <span className={cn("block", className)}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.12em]">
          <motion.span
            className={cn("block", lineClassName)}
            initial={{ y: "110%" }}
            {...viewProps}
            transition={{ duration: 0.9, ease: EASE, delay: delay + i * 0.09 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

export const AnimatedCounter = ({ to, duration = 2, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  return <span ref={ref} className={className}>{val}</span>;
};

export const CtaButton = ({
  children,
  onClick,
  variant = "gold",
  size = "md",
  className = "",
  ...props
}) => {
  const base =
    "group relative inline-flex items-center justify-center gap-2 font-semibold tracking-wide rounded-full transition-[transform,background-color,box-shadow,color] duration-300 will-change-transform active:scale-[0.98]";
  const sizes = {
    sm: "px-5 py-2.5 text-xs",
    md: "px-7 py-3.5 text-sm",
    lg: "px-9 py-4 text-sm md:text-base",
  };
  const variants = {
    gold:
      "bg-brand-gold text-brand-navy shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)] hover:-translate-y-0.5 hover:bg-[#e0be48] hover:shadow-[0_16px_40px_-10px_rgba(212,175,55,0.7)]",
    navy:
      "bg-brand-navy text-white hover:-translate-y-0.5 hover:bg-brand-blue shadow-[0_10px_30px_-10px_rgba(10,25,47,0.6)]",
    outline:
      "border border-brand-navy/25 text-brand-navy hover:border-brand-navy/60 hover:-translate-y-0.5 bg-white/40",
    ghostLight:
      "border border-white/25 text-white hover:bg-white/10 hover:-translate-y-0.5",
  };
  return (
    <button
      onClick={onClick}
      className={cn(base, sizes[size], variants[variant], className)}
      {...props}
    >
      {children}
    </button>
  );
};

export const SectionLabel = ({ children, dark = false, className = "" }) => (
  <div className={cn("flex items-center gap-3", className)}>
    <span className={cn("h-px w-8", dark ? "bg-brand-gold/60" : "bg-brand-gold")} />
    <span
      className={cn(
        "text-[11px] font-semibold uppercase tracking-[0.28em]",
        dark ? "text-brand-gold" : "text-brand-goldDark"
      )}
    >
      {children}
    </span>
  </div>
);
